import { template } from "@ember/template-compiler";
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { dasherize } from '@ember/string';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { EmberChangeset } from 'ember-changeset';
import { Changeset } from 'ember-changeset';
import changesetSet from 'ember-changeset/helpers/changeset-set';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import WrButton from 'ember-wiser/components/wr/button';
import WrForm from 'ember-wiser/components/wr/form';
import WrLinkTo from 'ember-wiser/components/wr/link-to';
import TestEnvFeature from 'parog-web/components/test-env-feature';
import type ParogService from 'parog-web/services/parog';
import type SemeiaSessionService from 'parog-web/services/session';
import AuthUtils from 'parog-web/utils/authentication';
import LoginValidations from 'parog-web/validations/login';
import TwoFactorValidations from 'parog-web/validations/twoFactorAuth';
import emberSwal from 'ui-kit/utils/ember-swal';
import { lookupValidator } from 'validated-changeset';
interface Signature {
    Args: {
        onLoading?: (isLoading: boolean) => void;
        userType: 'medic' | 'patient';
    };
}
interface Model {
    email?: string;
    password?: string;
    twoFactorCode?: string;
}
type Credentials = {
    email: string;
    password: string;
    twoFactorCode?: string;
    twoFactorToken: string | null;
    browserAuthToken?: string | null;
};
export default class AuthenticationLogin extends Component<Signature> {
    @tracked
    twoFactorRequired = false;
    @tracked
    loginSuccessful = false;
    @tracked
    errorMessage: string | undefined;
    loginChangeset: EmberChangeset;
    twoFactorChangeset: EmberChangeset;
    authenticator: string;
    @service
    session: SemeiaSessionService;
    @service
    parog: ParogService;
    @tracked
    codeHasBeenSentBack = false;
    get shouldDisplaySendBackCodeLink() {
        return !this.codeHasBeenSentBack && !this.loginTask.isRunning;
    }
    @action
    onSendBackClick() {
        this.codeHasBeenSentBack = true;
        this.sendBackAuthCode();
    }
    constructor(owner1: unknown, args1){
        super(owner1, args1);
        this.authenticator = this.args.userType == 'medic' ? 'authenticator:medics-password' : 'authenticator:patients-password';
        const model1 = {};
        this.loginChangeset = Changeset(model1, lookupValidator(LoginValidations), LoginValidations);
        this.twoFactorChangeset = Changeset(model1, lookupValidator(TwoFactorValidations), TwoFactorValidations);
    }
    get data() {
        const data1 = this.twoFactorRequired ? this.twoFactorChangeset : this.loginChangeset;
        return data1 as Model;
    }
    get credentials(): Credentials {
        const creds1 = {
            email: this.loginChangeset.get('email'),
            password: this.loginChangeset.get('password'),
            twoFactorCode: this.twoFactorChangeset.get('twoFactorCode'),
            twoFactorToken: AuthUtils.get2faTokenFromStorage(this.loginChangeset.get('email'))
        };
        if (this.args.userType == 'medic') {
            Object.assign(creds1, {
                browserAuthToken: AuthUtils.getBrowserAuthToken(this.loginChangeset.get('email'))
            });
        }
        return creds1;
    }
    get isLoggingIn() {
        return this.loginSuccessful || this.loginTask.isRunning;
    }
    @action
    async sendBackAuthCode() {
        const credentials1 = this.credentials;
        // Don't send the twoFactorCode to generate a new code.
        delete credentials1.twoFactorCode;
        try {
            await this.session.authenticate(this.authenticator, credentials1);
        } catch  {
            emberSwal.fire({
                title: 'Un nouveau code vous a été envoyé.',
                text: 'Vérifier vos email et vos SMS.',
                icon: 'success'
            });
        }
    }
    loginTask = task(async ()=>{
        this.args.onLoading?.(true);
        try {
            await this.session.authenticate(this.authenticator, this.credentials);
            this.loginSuccessful = true;
        // No action should be done after `authenticate` as Ember Simple Auth
        // automatically transitions to the configured route (index by default).
        } catch (error1) {
            this.loginSuccessful = false;
            const { reason: reason1 } = error1;
            if (reason1?.status === 401) {
                const twoFactorStatus1 = reason1.headers.get(dasherize(AuthUtils.twoFactorHeaderKey));
                if (twoFactorStatus1 === 'required') {
                    // A 2FA can be required if our token has expired
                    AuthUtils.clear2faTokenInStorage(this.credentials.email);
                    this.twoFactorRequired = true;
                } else if (twoFactorStatus1 === 'invalid') {
                    this.errorMessage = 'Verification code is missing, invalid or expired';
                    emberSwal.fire('Oops !', 'Code de vérification invalide.', 'error');
                } else {
                    this.errorMessage = reason1.body;
                    emberSwal.fire('Oops !', 'Email ou mot de passe invalide.', 'error');
                }
            } else if (reason1?.status === 429) {
                const retryAfter1 = reason1.headers.get('retry-after');
                emberSwal.fire('Attention.', `Vous avez effectué trop de tentatives de connexion. Veuillez attendre ${retryAfter1} secondes et réessayer.`, 'error');
            } else {
                // Re-throw errors other than "bad credentials" to get them in Sentry.
                throw error1;
            }
        } finally{
            this.args.onLoading?.(false);
        }
    });
    get magicLinkEmails() {
        const magicLinkDeduppers1 = [
            '+nw',
            '+ow',
            '+mw',
            '+rw'
        ];
        if (this.args.userType == 'medic') {
            return magicLinkDeduppers1.flatMap((dedupper1)=>[
                    'doctor',
                    'nurse'
                ].map((e1)=>`${e1}${dedupper1}@semeia.io`));
        }
        return magicLinkDeduppers1.map((e1)=>`patient-zero${e1}@semeia.io`);
    }
    passwordForgottenRoute = `login.${this.args.userType}-password-forgotten`;
    static{
        template(`
    <WrForm
      @data={{this.data}}
      @onSubmit={{perform this.loginTask}}
      class='mx-auto flex w-full flex-col items-stretch gap-6 pb-2 sm:w-[355px]'
      as |form|
    >
      {{#if this.twoFactorRequired}}
        <p>
          Vous avez reçu un code à 4 chiffres par SMS ou par email. Merci de le
          renseigner ici&nbsp;:
        </p>

        <form.Input
          @name='twoFactorCode'
          type='password'
          placeholder='Code de sécurité'
          aria-label='Code de sécurité'
          data-test-login='2faCode'
        />

        <p>
          <span class='time-label'>
            Vous n'avez pas reçu de SMS ni d'email ?
          </span>
          {{#if this.shouldDisplaySendBackCodeLink}}
            <WrButton
              @variant='anchor'
              data-test-send-back-auth-code
              {{on 'click' this.onSendBackClick}}
            >
              Renvoyer le code
            </WrButton>
          {{/if}}
        </p>

        <p class='time-label'>
          En cas de problème, vous pouvez contacter le support de Sêmeia du
          lundi au vendredi entre 9h et 18h au
          <a
            href='mailto:ide@semeia.io?subject=Problème authentification double facteur'
          >
            ide@semeia.io
          </a>
        </p>
      {{else}}
        <form.Input
          @name='email'
          aria-label='Votre adresse e-mail'
          placeholder='Votre adresse e-mail'
          autocomplete='username'
        />

        <form.Input
          @name='password'
          type='password'
          aria-label='Votre mot de passe'
          placeholder='Votre mot de passe'
          autocomplete='current-password'
        />

      {{/if}}

      <form.SubmitButton @icon='ArrowRight' class='self-center'>Se connecter</form.SubmitButton>

      {{#unless this.twoFactorRequired}}
        <WrLinkTo @route={{this.passwordForgottenRoute}} class='self-center'>
          J’ai oublié mon mot de passe
        </WrLinkTo>

        <TestEnvFeature>
          <div class='text-center'>
            {{#each this.magicLinkEmails as |email|}}
              <WrButton
                class='text-sm sm:text-base'
                data-test-magic-link={{email}}
                type='submit'
                @variant='anchor'
                {{on
                  'click'
                  (fn (changesetSet this.loginChangeset 'email') email)
                }}
                {{on
                  'click'
                  (fn
                    (changesetSet this.loginChangeset 'password') 'SEMEIApass!'
                  )
                }}
              >
                ✨ Login as
                {{email}}
                ✨
              </WrButton>
            {{/each}}
          </div>
        </TestEnvFeature>
      {{/unless}}
    </WrForm>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
